// /status/:teamId — single-workspace detail (Pass 5.2).
//
// Bookmarkable. The card design matches the per-workspace card
// shape Pass 5.1 introduced — same field grid, same error
// callout, same theme tokens. Polling still comes from the
// shared SystemStatusContext, so opening this URL in a new tab
// kicks off the same 60s ambient poll the list page uses.
//
// "Unknown team_id" is rendered as an empty state with a link
// back to /status — distinct from "loading", which renders
// nothing while the first poll is in flight.

import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useSystemStatus } from "../state/SystemStatusContext";
import { WorkspaceDetailCard } from "../components/WorkspaceDetailCard";
import { ScopeSelector } from "../components/ScopeSelector";
import { HomeButton } from "../components/HomeButton";
import { SignOutButton } from "../components/SignOutButton";

export function StatusDetailPage() {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const { status, error } = useSystemStatus();

  const workspace = status?.workspaces.find((w) => w.team_id === teamId);

  return (
    <div
      data-testid="status-detail-page"
      data-team-id={teamId}
      className="min-h-screen text-theme-text-primary"
    >
      {/* S4.3: breadcrumb replaces the standalone back-link —
          "System status / <workspace>". The crumb link keeps the
          status-detail-back testid + /status href. */}
      <header className="flex items-center justify-between px-section py-default border-b border-theme-border-default">
        <div className="flex items-center gap-snug">
          <HomeButton />
          <nav aria-label="Breadcrumb" className="flex items-center gap-snug">
            <Link
              to="/status"
              data-testid="status-detail-back"
              className="inline-flex items-center gap-1.5 text-caption text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={1.5} aria-hidden />
              System status
            </Link>
            <span aria-hidden className="text-theme-text-tertiary">/</span>
            <h1
              data-testid="status-detail-title"
              className="text-h3"
            >
              {workspace ? workspace.team_name : "Workspace"}
            </h1>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <ScopeSelector />
          <SignOutButton />
        </div>
      </header>

      <main className="px-section py-section max-w-4xl mx-auto">
        {error && !status && (
          <div
            data-testid="status-detail-error"
            className="bg-theme-error-bg border border-theme-error-border text-theme-error-text px-4 py-3 text-sm"
          >
            Failed to load /system/status: {error.message} (HTTP {error.status})
          </div>
        )}

        {status && !workspace && (
          <div
            data-testid="status-detail-not-found"
            className="bg-theme-bg-card border border-theme-border-default px-5 py-6 text-sm text-theme-text-secondary"
          >
            <div className="mb-3 font-mono">
              Workspace{" "}
              <code className="text-theme-text-primary">{teamId}</code> not
              found.
            </div>
            <div className="text-theme-text-tertiary font-mono text-xs">
              It may have been removed, or the data hasn't loaded yet.
            </div>
            <Link
              to="/status"
              className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 border border-theme-border-default font-mono text-[10px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary transition-colors duration-150"
            >
              Back to system status
            </Link>
          </div>
        )}

        {status && workspace && (
          <WorkspaceDetailCard
            workspace={workspace}
            driveQueue={status.drive_queue}
          />
        )}
      </main>
    </div>
  );
}
